import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { useLockBodyScroll } from 'react-use'
import LoaderImg from '../../images/loader.png'

const FormProgressOverlay = ({ initialTimeout = 0, state, onClose }) => {
  const interval = useRef()
  const [timeoutSeconds, setTimeoutSeconds] = useState(initialTimeout)
  const show = state !== 'default'

  useLockBodyScroll(show)

  useEffect(() => {
    if (state === 'success') {
      setTimeoutSeconds(5)
      interval.current = setInterval(() => {
        setTimeoutSeconds(s => s - 1)
      }, 1000)
    } else {
      clearInterval(interval.current)
    }
  }, [state])

  useEffect(() => {
    if (timeoutSeconds < 1) {
      clearInterval(interval.current)
      onClose()
    }
  }, [timeoutSeconds])

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[999] backdrop-blur-sm fle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
        >
          <div className="absolute inset-0 z-0 bg-black opacity-50"></div>
          <div className="absolute inset-0 z-10 flex flex-col justify-center item-center">
            <div className="container">
              <div className="z-50 row-type-g">
                <div className="col-1-type-g3">
                  <motion.div
                    className="flex flex-col items-center justify-center h-64 bg-white shadow-box rounded-[5px] gap-4 relative"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      type: 'ease',
                      ease: 'easeInOut',
                      delay: 0.2,
                      duration: 0.3,
                    }}
                  >
                    {['success', 'error'].includes(state) && (
                      <motion.button
                        type="button"
                        className="absolute z-50 top-2 right-2"
                        onClick={onClose}
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          type: 'ease',
                          ease: 'easeOut',
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="30"
                          height="30"
                          fill="none"
                          viewBox="0 0 30 30"
                        >
                          <circle cx="15" cy="15" r="15" fill="#000"></circle>
                          <path
                            fill="#fff"
                            fillRule="evenodd"
                            d="M8.293 20.293a1 1 0 101.414 1.414L15 16.414l5.293 5.293a1 1 0 001.414-1.414L16.414 15l5.293-5.293a1 1 0 00-1.414-1.414L15 13.586 9.707 8.293a1 1 0 00-1.414 1.414L13.586 15l-5.293 5.293z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                      </motion.button>
                    )}
                    <AnimatePresence>
                      {state === 'loading' && (
                        <motion.div
                          key="loading"
                          className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                          initial={{ opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{
                            y: -60,
                            opacity: 0,
                            transition: {
                              type: 'ease',
                              ease: 'easeIn',
                              delay: 0,
                            },
                          }}
                        >
                          <img
                            src={LoaderImg}
                            alt=""
                            role="presentation"
                            className="select-none w-7 h-7 animate-spin"
                          />
                          <p className="font-bold text-center select-none u-p">
                            Ihre Nachricht wird gesendet.
                          </p>
                        </motion.div>
                      )}
                      {state === 'success' && (
                        <motion.div
                          key="success"
                          className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                          initial={{ y: 60, opacity: 0 }}
                          animate={{
                            y: 0,
                            opacity: 1,
                            transition: {
                              type: 'ease',
                              ease: 'easeOut',
                              delay: 0.2,
                            },
                          }}
                          exit={{
                            y: -60,
                            opacity: 0,
                            transition: {
                              type: 'ease',
                              ease: 'easeIn',
                              delay: 0,
                            },
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="36"
                            height="36"
                            fill="none"
                            viewBox="0 0 36 36"
                            className="w-auto h-9"
                          >
                            <path
                              stroke="#000"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="5"
                              d="M3.857 17.43l9.416 9.921L31.909 7.715"
                            ></path>
                          </svg>
                          <div className="flex flex-col items-center gap-2 text-center">
                            <motion.p
                              className="font-bold select-none u-p"
                              initial={{ y: 25 }}
                              animate={{
                                y: 0,
                                transition: { type: 'ease', ease: 'easeOut' },
                              }}
                              exit={{
                                y: 5,
                                transition: { type: 'ease', ease: 'easeIn' },
                              }}
                            >
                              Danke. Ihre Nachricht wurde gesendet.
                            </motion.p>
                            <motion.p
                              className="u-p-small"
                              initial={{ y: 50 }}
                              animate={{
                                y: 0,
                                transition: { type: 'ease', ease: 'easeOut' },
                              }}
                              exit={{
                                y: 10,
                                transition: { type: 'ease', ease: 'easeIn' },
                              }}
                            >
                              Dieses Fenster wird in {timeoutSeconds || 1}{' '}
                              {timeoutSeconds > 1 ? 'Sekunden' : 'Sekunde'}{' '}
                              geschlossen.
                            </motion.p>
                            <motion.div
                              className="w-[200px] h-1 bg-gray-3 rounded-full mt-3 relative"
                              initial={{ y: 50 }}
                              animate={{
                                y: 0,
                                transition: { type: 'ease', ease: 'easeOut' },
                              }}
                              exit={{
                                y: 10,
                                transition: { type: 'ease', ease: 'easeIn' },
                              }}
                            >
                              <motion.div
                                className="absolute top-0 left-0 h-full transition-all bg-black rounded-full"
                                animate={{
                                  width: timeoutSeconds > 0 ? '100%' : '0%',
                                }}
                                transition={{
                                  type: 'tween',
                                  ease: 'linear',
                                  duration: 5,
                                }}
                              />
                            </motion.div>
                          </div>
                        </motion.div>
                      )}
                      {state === 'error' && (
                        <motion.div
                          key="error"
                          className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                          initial={{ y: 60, opacity: 0 }}
                          animate={{
                            y: 0,
                            opacity: 1,
                            transition: {
                              type: 'ease',
                              ease: 'easeOut',
                              delay: 0.2,
                            },
                          }}
                          exit={{
                            y: -60,
                            opacity: 0,
                            transition: {
                              type: 'ease',
                              ease: 'easeIn',
                              delay: 0,
                            },
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="36"
                            height="36"
                            fill="none"
                            viewBox="0 0 36 36"
                            className="w-auto h-9"
                          >
                            <path
                              fill="#FF1010"
                              d="M15.876 6.422c.978-1.575 3.27-1.575 4.248 0l13.98 22.523c.827 1.333-.131 3.055-1.7 3.055H3.596c-1.568 0-2.526-1.722-1.699-3.055l13.98-22.523z"
                            ></path>
                            <path
                              fill="#fff"
                              d="M19.678 13v8.828h-3.356V13h3.356zM16 25.77c0-.552.2-1.004.598-1.356A2.049 2.049 0 0118 23.885c.536 0 1.004.176 1.402.529.399.352.598.804.598 1.356s-.2 1.004-.598 1.356a2.05 2.05 0 01-1.402.53 2.05 2.05 0 01-1.402-.53c-.399-.352-.598-.804-.598-1.356z"
                            ></path>
                          </svg>
                          <div className="flex flex-col items-center gap-2 text-center">
                            <motion.p
                              className="font-bold select-none u-p"
                              initial={{ y: 25 }}
                              animate={{
                                y: 0,
                                transition: { type: 'ease', ease: 'easeOut' },
                              }}
                              exit={{
                                y: 5,
                                transition: { type: 'ease', ease: 'easeIn' },
                              }}
                            >
                              Entschuldigung, da war ein Fehler.
                            </motion.p>
                            <motion.p
                              className="u-p-small"
                              initial={{ y: 50 }}
                              animate={{
                                y: 0,
                                transition: { type: 'ease', ease: 'easeOut' },
                              }}
                              exit={{
                                y: 10,
                                transition: { type: 'ease', ease: 'easeIn' },
                              }}
                            >
                              Bitte versuche es erneut.
                            </motion.p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default FormProgressOverlay
